import { yupResolver } from '@hookform/resolvers/yup'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { array, object, string } from 'yup'

import { partnerFieldValidator } from '@percent/product-nomination-form/app/HostedForm/HostedForm/partnerFieldValidator/partnerFieldValidator'
import { isValidWebsiteURL } from '@percent/product-nomination-form/app/HostedForm/HostedForm/websiteValidator/websiteValidator'
import { usePartnerConfig } from '@percent/product-nomination-form/hooks'

import {
  BaseSubmitNominationFormPayload,
  SubmitNominationFormPayload,
} from '@percent/product-nomination-form/api/nominationForm/nominationForm.types'
import { FormContextControllerProps } from './FormContextController.types'

export const FormContextController = ({
  children,
  customDefaultValues,
}: FormContextControllerProps) => {
  const { partnerConfig: nominationConfig } = usePartnerConfig()
  const { t } = useTranslation()

  const hostedFormSchema = object({
    productNominationInviteId: string().required(),
    productNominationConfigurationId: string().required(),

    nominator: object({
      fullName: string()
        .required(
          t('hostedValidation.validation.required', {
            fieldName: t('hostedValidation.nominatorDetails.fullName'),
            interpolation: { escapeValue: false },
          })
        )
        .max(
          255,
          t('hostedValidation.validation.maxLength', {
            fieldName: t('hostedValidation.nominatorDetails.fullName'),
            maxCharacterLimit: 255,
            interpolation: { escapeValue: false },
          })
        ),
      email: string()
        .required(
          t('hostedValidation.validation.required', {
            fieldName: t('hostedValidation.nominatorDetails.email'),
            interpolation: { escapeValue: false },
          })
        )
        .email(t('hostedValidation.validation.emailValid'))
        .max(
          255,
          t('hostedValidation.validation.maxLength', {
            fieldName: t('hostedValidation.nominatorDetails.email'),
            maxCharacterLimit: 255,
            interpolation: { escapeValue: false },
          })
        ),
    }),

    nominees: array().of(
      object({
        firstName: string().required(
          t('hostedValidation.validation.required', {
            fieldName: t('hostedValidation.label.firstName'),
            interpolation: { escapeValue: false },
          })
        ),
        lastName: string().required(
          t('hostedValidation.validation.required', {
            fieldName: t('hostedValidation.label.lastName'),
            interpolation: { escapeValue: false },
          })
        ),
        email: string()
          .email(t('hostedValidation.validation.emailValid'))
          .required(
            t('hostedValidation.validation.required', {
              fieldName: t('hostedValidation.label.email'),
              interpolation: { escapeValue: false },
            })
          ),
        validationSubmissionConfigurationIds: array()
          .of(string())
          .required(
            t('hostedValidation.validation.required', {
              fieldName: t('hostedValidation.label.products'),
              interpolation: { escapeValue: false },
            })
          )
          .min(1, t('hostedValidation.validation.minSelection', { count: 1 })),
        // TODO: remove transform whenever we will move away from hardcoded language
        language: string().transform(() => 'en'),
      })
    ),

    organisationId: string(),

    // Custom organisation fields

    countryCode: string().when('organisationId', {
      is: '',
      then: string()
        .required(
          t('hostedValidation.validation.required', {
            fieldName: t('hostedValidation.label.countryCode'),
            interpolation: { escapeValue: false },
          })
        )
        .max(
          255,
          t('hostedValidation.validation.maxLength', {
            fieldName: t('hostedValidation.label.countryCode'),
            maxCharacterLimit: 255,
            interpolation: { escapeValue: false },
          })
        ),
      otherwise: string(),
    }),

    organisationName: string().when('organisationId', {
      is: '',
      then: string()
        .required(
          t('hostedValidation.validation.required', {
            fieldName: t('hostedValidation.label.organizationName'),
            interpolation: { escapeValue: false },
          })
        )
        .max(
          255,
          t('hostedValidation.validation.maxLength', {
            fieldName: t('hostedValidation.label.organizationName'),
            maxCharacterLimit: 255,
            interpolation: { escapeValue: false },
          })
        ),
    }),

    website: isValidWebsiteURL(t('hostedValidation.validation.websiteValid'))
      .when('registryName', {
        is: (value: string) => !!value,
        then: string()
          .required(
            t('hostedValidation.validation.required', {
              fieldName: t('hostedValidation.label.website'),
              interpolation: { escapeValue: false },
            })
          )
          .max(
            255,
            t('hostedValidation.validation.maxLength', {
              fieldName: t('hostedValidation.label.website'),
              maxCharacterLimit: 255,
              interpolation: { escapeValue: false },
            })
          ),
      })
      .max(
        255,
        t('hostedValidation.validation.maxLength', {
          fieldName: t('hostedValidation.label.website'),
          maxCharacterLimit: 255,
          interpolation: { escapeValue: false },
        })
      ),
    registryName: string().when('organisationId', {
      is: '',
      then: string()
        .required(
          t('hostedValidation.validation.required', {
            fieldName: t('hostedValidation.label.registryName'),
            interpolation: { escapeValue: false },
          })
        )
        .max(
          255,
          t('hostedValidation.validation.maxLength', {
            fieldName: t('hostedValidation.label.registryName'),
            maxCharacterLimit: 255,
            interpolation: { escapeValue: false },
          })
        ),
    }),
    registryId: string().when('organisationId', {
      is: '',
      then: string()
        .required(
          t('hostedValidation.validation.required', {
            fieldName: t('hostedValidation.label.registryId'),
            interpolation: { escapeValue: false },
          })
        )
        .max(
          255,
          t('hostedValidation.validation.maxLength', {
            fieldName: t('hostedValidation.label.registryId'),
            maxCharacterLimit: 255,
            interpolation: { escapeValue: false },
          })
        ),
    }),
  })

  let schema = hostedFormSchema

  nominationConfig?.configuration?.partnerFields?.map((field) => {
    schema = schema.concat(
      object({
        partnerFields: object({
          [field.id]: partnerFieldValidator(field, t),
        }),
      })
    )
  })

  const defaultValues: BaseSubmitNominationFormPayload = {
    productNominationConfigurationId: '',
    productNominationInviteId: '',
    nominator: {
      fullName: '',
      email: '',
    },
    nominees: [
      {
        firstName: '',
        lastName: '',
        email: '',
        validationSubmissionConfigurationIds: [],
        language: '',
      },
    ],
    ...customDefaultValues,
  }

  const methods = useForm<SubmitNominationFormPayload>({
    defaultValues,
    resolver: yupResolver(schema),
    reValidateMode: 'onBlur',
    mode: 'all',
  })

  return <FormProvider {...methods}>{children}</FormProvider>
}
